
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface RecentOrder {
  order_id: string;
  user_email: string;
  amount: number;
  status: string;
  created_at: string;
  product_name: string;
}

interface RecentOrdersTableProps {
  orders: RecentOrder[];
}

const RecentOrdersTable = ({ orders }: RecentOrdersTableProps) => {
  // Helper function to standardize status display
  const getStatusDisplay = (status: string) => {
    const normalizedStatus = (status || '').toUpperCase();
    
    // Group success statuses
    if (['PAID', 'SUCCESS', 'PAYMENT_SUCCESS', 'PAYMENT_LINK_PAID', 'ACTIVE'].includes(normalizedStatus)) {
      return 'PAID';
    }
    
    // Group pending statuses
    if (['PENDING', 'INITIATED', 'PAYMENT_INITIATED', 'PAYMENT_LINK_CREATED'].includes(normalizedStatus)) {
      return 'PENDING';
    }
    
    // Group failed statuses
    if (['FAILED', 'PAYMENT_FAILED', 'EXPIRED', 'CANCELLED', 'PAYMENT_LINK_EXPIRED', 'PAYMENT_LINK_CANCELLED'].includes(normalizedStatus)) {
      return 'FAILED';
    }
    
    // Default fallback
    return status || 'Unknown';
  };
  
  // Helper function to get appropriate status styling
  const getStatusStyling = (status: string) => {
    const displayStatus = getStatusDisplay(status);
    
    if (displayStatus === 'PAID') {
      return 'bg-green-100 text-green-800';
    } else if (displayStatus === 'PENDING') {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-red-100 text-red-800';
    }
  };
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order ID</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-right">Created At</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-4">
                No recent orders found
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order) => (
              <TableRow key={order.order_id} className="hover:bg-muted/20">
                <TableCell className="font-mono text-xs">
                  {order.order_id.length > 16 ? order.order_id.substring(0, 16) + '...' : order.order_id}
                </TableCell>
                <TableCell>
                  {order.user_email}
                </TableCell>
                <TableCell>
                  {order.product_name || 'N/A'}
                </TableCell>
                <TableCell className="text-right">
                  ₹{order.amount}
                </TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusStyling(order.status)}`}>
                    {getStatusDisplay(order.status)}
                  </span>
                </TableCell>
                <TableCell className="text-right text-xs">
                  {new Date(order.created_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default RecentOrdersTable;
